import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  buildBillOfLadingOpenPath,
  buildManifestOpenPath,
  buildShippingDocumentClosePath,
  parseShippingDocumentIdParam,
} from "./manifestsV3DrawerNavigation";

/**
 * Owns the URL wiring for `ManifestsV3`'s two row-click drawers: the manifest
 * drawer (path segment `processedManifestId`, read by the caller via
 * `useSelectedIdInUrl`) and the bill-of-lading drawer (query param
 * `shippingDocumentId`, read and written here). Opening either one clears
 * the other's URL state first — via the `build*OpenPath` helpers in
 * `manifestsV3DrawerNavigation.ts` — so the two drawers can never both claim
 * to be open.
 */
export function useManifestsV3RowDrawers(processedManifestId: string | undefined) {
  const navigate = useNavigate();
  const location = useLocation();

  const shippingDocumentId = useMemo(
    () => parseShippingDocumentIdParam(location.search),
    [location.search]
  );

  const openManifest = useCallback(
    /** `section` deep-links the drawer to one of its sections; omit for the top. */
    (manifestId: string, section?: string) => {
      navigate(
        buildManifestOpenPath({
          pathname: location.pathname,
          search: location.search,
          currentManifestId: processedManifestId,
          manifestId,
          section,
        }),
        { replace: !!processedManifestId, preventScrollReset: true }
      );
    },
    [navigate, location.pathname, location.search, processedManifestId]
  );

  const openBillOfLading = useCallback(
    (shippingDocumentIdToOpen: string) => {
      navigate(
        buildBillOfLadingOpenPath({
          pathname: location.pathname,
          search: location.search,
          currentManifestId: processedManifestId,
          shippingDocumentId: shippingDocumentIdToOpen,
        }),
        { replace: !!shippingDocumentId, preventScrollReset: true }
      );
    },
    [navigate, location.pathname, location.search, processedManifestId, shippingDocumentId]
  );

  const closeShippingDocument = useCallback(() => {
    navigate(
      buildShippingDocumentClosePath({ pathname: location.pathname, search: location.search }),
      { preventScrollReset: true }
    );
  }, [navigate, location.pathname, location.search]);

  return { shippingDocumentId, openManifest, openBillOfLading, closeShippingDocument };
}
